import { ApiFunction, ChatbotApi } from "../chatbot/towersChatbotApi";
import auth from "../../utils/authentication";
import { ApiCallError } from "../torrestream-api/torresstream-api";
import { UserSummary } from "../squadflix-friends/SquadflixFriendsApi";

export interface ChatMessage {
    id: string;
    room_id: string;
    sender_uid: string;
    content: string;
    has_attachment: boolean;
    created_at: string;
    sender: UserSummary;
    attachment?: {
        id: string;
        file_name: string;
        file_size: number;
        mime_type: string;
    } | null;
}

export interface ChatRoomDetails {
    id: string;
    name?: string | null;
    is_group: boolean;
    photoUrl?: string | null;
    created_by: string;
    created_at: string;
    members: UserSummary[];
    lastMessage?: {
        id: string;
        content: string;
        created_at: string;
        sender: UserSummary;
    } | null;
}

export class SquadflixChatApi {
    static async createPrivateChat({ targetUid }: { targetUid: string }): Promise<{ success: boolean; roomId: string; message: string }> {
        const fn = new ApiFunction('chat-createPrivateChat', ChatbotApi.baseUrl);
        return fn.call({ targetUid });
    }

    static async createChatGroup({ name, invitedUids }: { name: string; invitedUids?: string[] }): Promise<{ success: boolean; roomId: string; addedMembers: string[]; message: string }> {
        const fn = new ApiFunction('chat-createChatGroup', ChatbotApi.baseUrl);
        return fn.call({ name, invitedUids });
    }

    static async addMemberToGroup({ roomId, targetUid }: { roomId: string; targetUid: string }): Promise<{ success: boolean; message: string }> {
        const fn = new ApiFunction('chat-addMemberToGroup', ChatbotApi.baseUrl);
        return fn.call({ roomId, targetUid });
    }

    static async sendMessage({ roomId, messageText, file }: { roomId: string; messageText?: string; file?: File }): Promise<{ success: boolean; messageId: string; message: string }> {
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const headers: Record<string, string> = {};
        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }

        const formData = new FormData();
        formData.append("roomId", roomId);
        if (messageText !== undefined) {
            formData.append("messageText", messageText);
        }
        if (file) {
            formData.append("file", file);
        }

        const response = await fetch(`${ChatbotApi.baseUrl}chat-sendMessage`, {
            method: "POST",
            headers,
            body: formData,
        });

        if (response.ok) {
            return response.json();
        } else {
            throw new ApiCallError(await response.text(), response.status);
        }
    }

    static async getChatMessages({ roomId, limit, offset }: { roomId: string; limit?: number; offset?: number }): Promise<{ success: boolean; messages: ChatMessage[] }> {
        const fn = new ApiFunction('chat-getChatMessages', ChatbotApi.baseUrl);
        return fn.call({ roomId, limit, offset });
    }

    static async listChats(): Promise<{ success: boolean; chats: ChatRoomDetails[] }> {
        const fn = new ApiFunction('chat-listChats', ChatbotApi.baseUrl);
        return fn.call();
    }

    static async getChatAttachment({ attachmentId }: { attachmentId: string }): Promise<Response> {
        const fn = new ApiFunction('chat-getChatAttachment', ChatbotApi.baseUrl);
        return fn.call({ attachmentId });
    }

    static async leaveChatGroup({ roomId }: { roomId: string }): Promise<any> {
        const fn = new ApiFunction('chat-leaveChatGroup', ChatbotApi.baseUrl);
        return fn.call({ roomId });
    }

    static async getOnlineFriends(): Promise<{ success: boolean; onlineFriends: UserSummary[] }> {
        const fn = new ApiFunction('chat-getOnlineFriends', ChatbotApi.baseUrl);
        return fn.call();
    }

    static async updateGroupDetails({ roomId, name, file }: { roomId: string; name?: string; file?: File | null }): Promise<{ success: boolean; message: string; name?: string; photoUrl?: string | null }> {
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const headers: Record<string, string> = {};
        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }

        const formData = new FormData();
        formData.append("roomId", roomId);
        if (name !== undefined) {
            formData.append("name", name);
        }
        if (file) {
            formData.append("file", file);
        }

        const response = await fetch(`${ChatbotApi.baseUrl}chat-updateGroupDetails`, {
            method: "POST",
            headers,
            body: formData,
        });

        if (response.ok) {
            return response.json();
        } else {
            throw new ApiCallError(await response.text(), response.status);
        }
    }

    static async getGroupPhoto({ roomId }: { roomId: string }): Promise<Response> {
        const fn = new ApiFunction('chat-getGroupPhoto', ChatbotApi.baseUrl);
        return fn.call({ roomId });
    }
}
